'use client';

import { FeedbackInviteMode } from '@up2stay/up2promo-api-client';
import { cn, Label, RadioGroup, RadioGroupItem } from '@up2stay/up2promo-ui';

import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';

export type FeedbackModeCardSelectorLabels = {
  feedbackOnly: string;
  feedbackOnlyDescription: string;
  feedbackThenReview: string;
  feedbackThenReviewDescription: string;
  directReview: string;
  directReviewDescription: string;
  needsLocation: string;
  needsGoogle: string;
};

type FeedbackModeCardSelectorProps = {
  value: FeedbackInviteMode;
  onValueChange: (mode: FeedbackInviteMode) => void;
  googleAvailable: boolean;
  hasLocation: boolean;
  labels: FeedbackModeCardSelectorLabels;
};

function modeNeedsGoogle(mode: FeedbackInviteMode): boolean {
  return (
    mode === FeedbackInviteMode.FEEDBACK_THEN_OPTIONAL_REVIEW ||
    mode === FeedbackInviteMode.DIRECT_REVIEW
  );
}

function ModeOption({
  value,
  id,
  title,
  description,
  disabled,
}: {
  value: FeedbackInviteMode;
  id: string;
  title: string;
  description: string;
  disabled?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-[var(--r-control)] border border-border/80 bg-card pr-1 transition-colors',
        disabled
          ? 'opacity-55'
          : 'has-[[data-state=checked]]:border-[color:var(--ghost-accent-border)] has-[[data-state=checked]]:bg-[color:var(--ghost-accent-bg)] has-[[data-state=checked]]:text-[color:var(--ghost-accent-fg)]',
      )}
    >
      <Label
        htmlFor={id}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <RadioGroupItem
          value={value}
          id={id}
          disabled={disabled}
          className="shrink-0"
        />
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
          {title}
        </span>
      </Label>
      <SettingsInfoHint label={title}>{description}</SettingsInfoHint>
    </div>
  );
}

/** Card-style mode picker; Google modes disabled without a linked place. */
export function FeedbackModeCardSelector({
  value,
  onValueChange,
  googleAvailable,
  hasLocation,
  labels,
}: FeedbackModeCardSelectorProps) {
  const googleModesDisabled = !googleAvailable;

  return (
    <div className="space-y-2">
      <RadioGroup
        value={value}
        onValueChange={(next) => {
          const mode = next as FeedbackInviteMode;
          if (modeNeedsGoogle(mode) && googleModesDisabled) return;
          onValueChange(mode);
        }}
        className="flex flex-col gap-2"
      >
        <ModeOption
          value={FeedbackInviteMode.FEEDBACK_ONLY}
          id="feedback-mode-feedback-only"
          title={labels.feedbackOnly}
          description={labels.feedbackOnlyDescription}
        />
        <ModeOption
          value={FeedbackInviteMode.FEEDBACK_THEN_OPTIONAL_REVIEW}
          id="feedback-mode-then-review"
          title={labels.feedbackThenReview}
          description={labels.feedbackThenReviewDescription}
          disabled={googleModesDisabled}
        />
        <ModeOption
          value={FeedbackInviteMode.DIRECT_REVIEW}
          id="feedback-mode-direct-review"
          title={labels.directReview}
          description={labels.directReviewDescription}
          disabled={googleModesDisabled}
        />
      </RadioGroup>
      {googleModesDisabled ? (
        <p className="px-1 text-[12px] leading-snug text-muted-foreground/85">
          {hasLocation ? labels.needsGoogle : labels.needsLocation}
        </p>
      ) : null}
    </div>
  );
}

export function feedbackModeNeedsGoogle(mode: FeedbackInviteMode): boolean {
  return modeNeedsGoogle(mode);
}

export function locationHasGooglePlace(
  location:
    | { reviewExternalIds?: Record<string, string> | null }
    | null
    | undefined,
): boolean {
  return Boolean(location?.reviewExternalIds?.google?.trim());
}
